import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, GripVertical, Edit2, Trash2, Save, X, Eye } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { planApi, occasionApi, type TrainingPlan, type Occasion } from '../services/api';
import { VideoSelector } from './VideoSelector';

interface PlanBuilderProps {
  planId?: string;
  onSave?: (plan: TrainingPlan) => void;
  onCancel?: () => void;
}

interface OccasionItemProps {
  occasion: Occasion;
  onEdit: (occasion: Occasion) => void;
  onDelete: (occasionId: string) => void;
  onManageVideos: (occasion: Occasion) => void;
}

function OccasionItem({ occasion, onEdit, onDelete, onManageVideos }: OccasionItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: occasion.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-white border border-gray-200 rounded-lg p-4 flex items-center gap-3"
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
      >
        <GripVertical className="w-5 h-5" />
      </button>

      <div className="flex-1 min-w-0">
        <h4 className="font-medium text-gray-900 truncate">{occasion.title}</h4>
        {occasion.description && (
          <p className="text-sm text-gray-600 truncate">{occasion.description}</p>
        )}
        <p className="text-xs text-gray-500 mt-1">
          {occasion.estimated_duration_minutes} minutes
        </p>
      </div>

      <div className="flex gap-2">
        <button
          onClick={() => onManageVideos(occasion)}
          className="p-2 text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
          title="Manage videos"
        >
          <Eye className="w-4 h-4" />
        </button>
        <button
          onClick={() => onEdit(occasion)}
          className="p-2 text-gray-600 hover:bg-gray-50 rounded transition-colors"
          title="Edit occasion"
        >
          <Edit2 className="w-4 h-4" />
        </button>
        <button
          onClick={() => onDelete(occasion.id)}
          className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
          title="Delete occasion"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

interface OccasionEditorModalProps {
  occasion?: Occasion;
  planId: string;
  onClose: () => void;
  onSave: () => void;
}

function OccasionEditorModal({ occasion, planId, onClose, onSave }: OccasionEditorModalProps) {
  const [formData, setFormData] = useState({
    title: occasion?.title || '',
    description: occasion?.description || '',
    estimated_duration_minutes: occasion?.estimated_duration_minutes || 30,
  });

  const createMutation = useMutation({
    mutationFn: (data: Partial<Occasion>) => occasionApi.create(planId, data),
    onSuccess: () => {
      onSave();
      onClose();
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<Occasion>) => occasionApi.update(occasion!.id, data),
    onSuccess: () => {
      onSave();
      onClose();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (occasion) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            {occasion ? 'Edit Occasion' : 'Add Occasion'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label htmlFor="occasion-title" className="block text-sm font-medium text-gray-700">
              Title
            </label>
            <input
              id="occasion-title"
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              required
              maxLength={200}
            />
          </div>

          <div>
            <label htmlFor="occasion-description" className="block text-sm font-medium text-gray-700">
              Description
            </label>
            <textarea
              id="occasion-description"
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              maxLength={1000}
            />
          </div>

          <div>
            <label htmlFor="occasion-duration" className="block text-sm font-medium text-gray-700">
              Estimated Duration (minutes)
            </label>
            <input
              id="occasion-duration"
              type="number"
              min="1"
              value={formData.estimated_duration_minutes}
              onChange={(e) =>
                setFormData({ ...formData, estimated_duration_minutes: parseInt(e.target.value) })
              }
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              required
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending || updateMutation.isPending}
              className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
            >
              {occasion ? 'Save Changes' : 'Add Occasion'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function PlanBuilder({ planId, onSave, onCancel }: PlanBuilderProps) {
  const queryClient = useQueryClient();
  const [planData, setPlanData] = useState({
    title: '',
    description: '',
    tier: 'free' as 'free' | 'standard' | 'premium',
    difficulty: 'beginner' as 'beginner' | 'intermediate' | 'advanced',
    duration_weeks: 4,
  });
  const [showOccasionEditor, setShowOccasionEditor] = useState(false);
  const [editingOccasion, setEditingOccasion] = useState<Occasion | undefined>();
  const [showVideoSelector, setShowVideoSelector] = useState(false);
  const [selectedOccasion, setSelectedOccasion] = useState<Occasion | undefined>();

  // Fetch existing plan if editing
  const { data: existingPlan } = useQuery({
    queryKey: ['plan', planId],
    queryFn: async () => {
      const response = await planApi.get(planId!);
      return response.data;
    },
    enabled: !!planId,
  });

  // Fetch occasions for the plan
  const { data: occasions = [], refetch: refetchOccasions } = useQuery({
    queryKey: ['occasions', planId],
    queryFn: async () => {
      const response = await planApi.getOccasions(planId!);
      return response.data;
    },
    enabled: !!planId,
  });

  // Set plan data when existing plan loads
  if (existingPlan && planData.title === '') {
    setPlanData({
      title: existingPlan.title,
      description: existingPlan.description,
      tier: existingPlan.tier,
      difficulty: existingPlan.difficulty,
      duration_weeks: existingPlan.duration_weeks || 4,
    });
  }

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Create plan mutation
  const createPlanMutation = useMutation({
    mutationFn: (data: Partial<TrainingPlan>) => planApi.create(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['plans'] });
      if (onSave) onSave(response.data);
    },
  });

  // Update plan mutation
  const updatePlanMutation = useMutation({
    mutationFn: (data: Partial<TrainingPlan>) => planApi.update(planId!, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['plans'] });
      queryClient.invalidateQueries({ queryKey: ['plan', planId] });
      if (onSave) onSave(response.data);
    },
  });

  // Publish plan mutation
  const publishMutation = useMutation({
    mutationFn: (tier: 'free' | 'standard' | 'premium') => planApi.publish(planId!, tier),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plan', planId] });
      queryClient.invalidateQueries({ queryKey: ['plans'] });
    },
  });

  // Delete occasion mutation
  const deleteOccasionMutation = useMutation({
    mutationFn: (occasionId: string) => occasionApi.delete(occasionId),
    onSuccess: () => {
      refetchOccasions();
    },
  });

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = occasions.findIndex((o) => o.id === active.id);
      const newIndex = occasions.findIndex((o) => o.id === over.id);

      const reorderedOccasions = arrayMove(occasions, oldIndex, newIndex);

      // Update order for each occasion
      reorderedOccasions.forEach((occasion, index) => {
        if (occasion.order !== index) {
          occasionApi.update(occasion.id, { order: index });
        }
      });

      // Optimistically update the UI
      queryClient.setQueryData(['occasions', planId], reorderedOccasions);
    }
  };

  const handleSavePlan = () => {
    if (planId) {
      updatePlanMutation.mutate(planData);
    } else {
      createPlanMutation.mutate(planData);
    }
  };

  const handlePublish = () => {
    if (planId) {
      publishMutation.mutate(planData.tier);
    }
  };

  const handleDeleteOccasion = (occasionId: string) => {
    if (confirm('Are you sure you want to delete this occasion?')) {
      deleteOccasionMutation.mutate(occasionId);
    }
  };

  const handleEditOccasion = (occasion: Occasion) => {
    setEditingOccasion(occasion);
    setShowOccasionEditor(true);
  };

  const handleAddOccasion = () => {
    setEditingOccasion(undefined);
    setShowOccasionEditor(true);
  };

  const handleManageVideos = (occasion: Occasion) => {
    setSelectedOccasion(occasion);
    setShowVideoSelector(true);
  };

  return (
    <div className="space-y-6">
      {/* Plan Metadata Form */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Plan Details</h2>

        <div className="space-y-4">
          <div>
            <label htmlFor="plan-title" className="block text-sm font-medium text-gray-700">
              Title
            </label>
            <input
              id="plan-title"
              type="text"
              value={planData.title}
              onChange={(e) => setPlanData({ ...planData, title: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              required
              maxLength={200}
            />
          </div>

          <div>
            <label htmlFor="plan-description" className="block text-sm font-medium text-gray-700">
              Description
            </label>
            <textarea
              id="plan-description"
              rows={4}
              value={planData.description}
              onChange={(e) => setPlanData({ ...planData, description: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              maxLength={2000}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label htmlFor="plan-tier" className="block text-sm font-medium text-gray-700">
                Subscription Tier
              </label>
              <select
                id="plan-tier"
                value={planData.tier}
                onChange={(e) =>
                  setPlanData({ ...planData, tier: e.target.value as typeof planData.tier })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              >
                <option value="free">Free</option>
                <option value="standard">Standard</option>
                <option value="premium">Premium</option>
              </select>
            </div>

            <div>
              <label htmlFor="plan-difficulty" className="block text-sm font-medium text-gray-700">
                Difficulty
              </label>
              <select
                id="plan-difficulty"
                value={planData.difficulty}
                onChange={(e) =>
                  setPlanData({
                    ...planData,
                    difficulty: e.target.value as typeof planData.difficulty,
                  })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              >
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
            </div>

            <div>
              <label htmlFor="plan-duration" className="block text-sm font-medium text-gray-700">
                Duration (weeks)
              </label>
              <input
                id="plan-duration"
                type="number"
                min="1"
                value={planData.duration_weeks}
                onChange={(e) =>
                  setPlanData({ ...planData, duration_weeks: parseInt(e.target.value) })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            {onCancel && (
              <button
                onClick={onCancel}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            )}
            <button
              onClick={handleSavePlan}
              disabled={createPlanMutation.isPending || updatePlanMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              Save Plan
            </button>
            {planId && existingPlan && (
              <button
                onClick={handlePublish}
                disabled={publishMutation.isPending}
                className={`px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white ${
                  existingPlan.is_published
                    ? 'bg-gray-600 hover:bg-gray-700'
                    : 'bg-green-600 hover:bg-green-700'
                } disabled:opacity-50`}
              >
                {existingPlan.is_published ? 'Unpublish' : 'Publish'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Occasions List */}
      {planId && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Occasions</h2>
            <button
              onClick={handleAddOccasion}
              className="flex items-center gap-2 px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
            >
              <Plus className="w-4 h-4" />
              Add Occasion
            </button>
          </div>

          {occasions.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p>No occasions yet. Add your first occasion to get started.</p>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={occasions.map((o) => o.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-3">
                  {occasions.map((occasion) => (
                    <OccasionItem
                      key={occasion.id}
                      occasion={occasion}
                      onEdit={handleEditOccasion}
                      onDelete={handleDeleteOccasion}
                      onManageVideos={handleManageVideos}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>
      )}

      {/* Modals */}
      {showOccasionEditor && planId && (
        <OccasionEditorModal
          occasion={editingOccasion}
          planId={planId}
          onClose={() => setShowOccasionEditor(false)}
          onSave={refetchOccasions}
        />
      )}

      {showVideoSelector && selectedOccasion && (
        <VideoSelector
          occasion={selectedOccasion}
          onClose={() => setShowVideoSelector(false)}
        />
      )}
    </div>
  );
}
